import React, { Fragment, useState } from "react";
import { H3, LI, P, UL } from "../../../../AbstractElements";
import { Brand, Availability, AddToCart, BuyNow } from "../../../../Constant";
import SocialIcons from "./SocialIcons";
import StarRatings from "./StarRating";
import { Row, Col, Card, CardBody, Button, Badge } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";

const ProductDetails = ({ event }) => {
  const [quantity, setQuantity] = useState(1);
  const history = useNavigate();

  // Add CSS for pulse animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleRegister = () => {
    // Handle event registration
    alert(`Đăng ký tham gia sự kiện: ${event.title}`);
  };

  const handleShare = () => {
    // Handle event sharing
    alert(`Chia sẻ sự kiện: ${event.title}`);
  };

  // Function to determine event status based on current time
  const getEventStatus = (event) => {
    if (!event || !event.startTime || !event.endTime) {
      return 'unknown';
    }

    const now = new Date();
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);

    if (now < startTime) {
      return 'upcoming';
    } else if (now >= startTime && now <= endTime) {
      return 'now';
    } else {
      return 'ended';
    }
  };

  // Function to calculate time remaining
  const getTimeRemaining = (targetTime) => {
    if (!targetTime) return 'Chưa xác định';
    
    const now = new Date();
    const target = new Date(targetTime);
    const diff = target - now;

    if (diff <= 0) return 'Đã qua';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} ngày ${hours} giờ`;
    } else if (hours > 0) {
      return `${hours} giờ ${minutes} phút`;
    } else {
      return `${minutes} phút`;
    }
  };

  const getStatusBadge = (event) => {
    const status = getEventStatus(event);
    
    switch (status) {
      case 'now':
        return <Badge color="success" style={{
          animation: 'pulse 2s infinite',
          boxShadow: '0 0 10px rgba(40, 167, 69, 0.3)'
        }}>
          <i className="fa fa-play me-1"></i>
          Đang diễn ra
        </Badge>;
      case 'ended':
        return <Badge color="secondary">
          <i className="fa fa-check me-1"></i>
          Đã kết thúc
        </Badge>;
      case 'upcoming':
        return <Badge color="info">
          <i className="fa fa-clock me-1"></i>
          Sắp diễn ra
        </Badge>;
      default:
        return <Badge color="warning">
          <i className="fa fa-question me-1"></i>
          Chưa xác định
        </Badge>;
    }
  };

  if (!event) {
    return (
      <Fragment>
        <Col xxl="5" className="box-col-6 order-xxl-0 order-1">
          <Card>
            <CardBody>
              <div className="text-center py-4">
                <p>Không có thông tin sự kiện</p>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Col xxl="5" className="box-col-6 order-xxl-0 order-1">
        <Card className="shadow-sm border-0">
          <CardBody className="p-4">
            {/* Header Section */}
            <div className="mb-4">
              <H3 className="mb-3 text-dark fw-bold">{event.title || "Sự kiện"}</H3>
              <div className="d-flex align-items-center gap-3 mb-3">
                {getStatusBadge(event)}
                <div className="d-flex align-items-center text-muted">
                  <i className="fa fa-users me-1"></i>
                  <small>{event.attendees || 0} đã tham gia</small>
                </div>
              </div>
            </div>

            {/* Date & Time Section */}
            <div className="mb-4 p-3 bg-light rounded-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fa fa-calendar text-primary me-2"></i>
                <span className="fw-semibold text-dark">{event.formattedDate || "Chưa xác định"}</span>
              </div>
              {event.formattedDateTime && (
                <div className="d-flex align-items-center mb-2">
                  <i className="fa fa-clock text-secondary me-2"></i>
                  <span className="text-muted">{event.formattedDateTime}</span>
                </div>
              )}
              {/* Time remaining indicator */}
              {getEventStatus(event) === 'upcoming' && (
                <div className="d-flex align-items-center text-info">
                  <i className="fa fa-hourglass-half me-2"></i>
                  <small>Bắt đầu sau: {getTimeRemaining(event.startTime)}</small>
                </div>
              )}
              {getEventStatus(event) === 'now' && (
                <div className="d-flex align-items-center text-success">
                  <i className="fa fa-hourglass-end me-2"></i>
                  <small>Kết thúc sau: {getTimeRemaining(event.endTime)}</small>
                </div>
              )}
            </div>

            {/* Event Type & Capacity */}
            <div className="mb-4">
              <div className="d-flex gap-2 flex-wrap">
                <span className={`badge ${event.type === 'online' ? 'bg-success' : 'bg-primary'} px-3 py-2 rounded-pill`}>
                  <i className={`fa ${event.type === 'online' ? 'fa-video-camera' : 'fa-map-marker'} me-1`}></i>
                  {event.type === 'online' ? 'Online' : 'Offline'}
                </span>
                <span className="badge bg-info px-3 py-2 rounded-pill">
                  <i className="fa fa-users me-1"></i>
                  {event.formattedMaxAttendees}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-4">
              <h6 className="text-dark mb-2">Mô tả sự kiện</h6>
              <P className="text-muted lh-base">{event.descriptionText || "Mô tả sự kiện sẽ được cập nhật sớm."}</P>
            </div>

            {/* Event Details */}
            <div className="mb-4">
              <h6 className="text-dark mb-3">Thông tin chi tiết</h6>
              <div className="row g-3">
                <div className="col-12">
                  <div className="d-flex align-items-start">
                    <i className="fa fa-map-marker text-primary me-3 mt-1"></i>
                    <div>
                      <small className="text-muted d-block">Địa điểm</small>
                      <span className="fw-medium">{event.location || "Chưa xác định"}</span>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-start">
                    <i className="fa fa-user text-success me-3 mt-1"></i>
                    <div>
                      <small className="text-muted d-block">Người tổ chức</small>
                      <span className="fw-medium text-success">{event.organizer || "Chưa xác định"}</span>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-start">
                    <i className="fa fa-tag text-info me-3 mt-1"></i>
                    <div>
                      <small className="text-muted d-block">Loại sự kiện</small>
                      <span className="fw-medium">{event.formattedType}</span>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-start">
                    <i className="fa fa-users text-warning me-3 mt-1"></i>
                    <div>
                      <small className="text-muted d-block">Số lượng tham gia</small>
                      <span className="fw-medium">{event.formattedMaxAttendees}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social & Rating */}
            <div className="mb-4">
              <Row>
                <Col md="6">
                  <h6 className="text-dark mb-2">Chia sẻ</h6>
                  <SocialIcons />
                </Col>
                <Col md="6">
                  <h6 className="text-dark mb-2">Đánh giá</h6>
                  <StarRatings />
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div className="d-grid gap-2">
              <Button 
                color="primary" 
                size="lg" 
                className="rounded-pill py-2" 
                onClick={handleRegister}
              >
                <i className="fa fa-user-plus me-2"></i>
                Đăng ký tham gia
              </Button>
              <div className="d-flex gap-2">
                <Button 
                  color="outline-success" 
                  className="flex-fill rounded-pill" 
                  onClick={handleShare}
                >
                  <i className="fa fa-share me-1"></i>
                  Chia sẻ
                </Button>
                <Button 
                  color="outline-secondary" 
                  className="flex-fill rounded-pill" 
                  onClick={() => history(-1)}
                >
                  <i className="fa fa-arrow-left me-1"></i>
                  Quay lại
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Fragment>
  );
};
export default ProductDetails;
